/**
 * _shared/constants.ts
 * ────────────────────────────────────────────────────────────────────────
 * Constantes reutilizables entre templates: fallbacks, defaults, rutas
 * API, sello NOM-247. Centralizadas para que ningún template hardcodee
 * strings mágicos.
 *
 * IMPORTANTE: las constantes de dominio (PLATFORM_DOMAIN, getAgentUrl,
 * etc.) viven en `@/constants/config.ts` — NO duplicarlas aquí. Aquí
 * solo lo específico del subsistema de templates.
 *
 * Creado: 17 Abril 2026
 * ────────────────────────────────────────────────────────────────────────
 */

import type { LegalDocType } from './types'

/**
 * Subdominio por defecto cuando la landing se sirve en localhost o IP
 * (sin subdominio real). Evita errores en desarrollo local.
 */
export const SUBDOMAIN_FALLBACK = 'demo'

/**
 * Delay en segundos para auto-abrir el ChatWidget cuando el template
 * no especifica uno propio. 0 = no auto-abrir.
 */
export const DEFAULT_CHAT_DELAY = 0

/**
 * Valor default de `show_ia` cuando el backend no lo expone.
 * - Todos los templates excepto Showcase: true
 * - Showcase (plan free, sin IA): pasa { defaultShowIA: false } a
 *   resolveChatConfig para sobrescribir este default.
 */
export const DEFAULT_SHOW_IA = true

/**
 * Lista canónica de tipos de documento legal. Útil para iterar o
 * validar strings recibidos.
 */
export const LEGAL_DOC_TYPES: readonly LegalDocType[] = [
  'aviso_privacidad',
  'terminos_condiciones',
  'derechos_consumidor',
  'info_nom247',
] as const

/**
 * Rutas del backend usadas por los templates. Funciones — no strings
 * sueltos — para evitar errores de concatenación y tipar los params.
 *
 * @example
 *   const url = API_ROUTES.publicProperties(subdomain)
 *   const res = await fetch(url)
 */
export const API_ROUTES = {
  publicAgent: (subdomain: string): string =>
    `/api/agents/subdomain/${subdomain}`,
  publicProperties: (subdomain: string): string =>
    `/api/agents/subdomain/${subdomain}/properties`,
  publicSellers: (subdomain: string): string =>
    `/api/agents/subdomain/${subdomain}/sellers`,
  nomSeal: (subdomain: string): string =>
    `/api/public/nom/seal/${subdomain}`,
  legalDocPreview: (docType: LegalDocType, subdomain: string): string =>
    `/api/public/nom/legal-docs/preview/${docType}?subdomain=${subdomain}`,
} as const

/**
 * URL pública de verificación del certificado NOM-247.
 * Solo se genera cuando el agente tiene sello activo y certificate_code.
 *
 * NOTA: el dominio `cernag.com` aparece únicamente en esta URL técnica
 * por necesidad de verificación. CERNAG NO aparece en copy visible del
 * sitio del agente — la autoridad comunicada al visitante es la norma
 * NOM-247, no la certificadora.
 */
export const NOM_CERT_VERIFY_URL = (certCode: string): string =>
  `https://cernag.com/certs/nom247/${certCode}`

// ══════════════════════════════════════════════════════════════════════════
// Sello de Cumplimiento NOM-247
// ══════════════════════════════════════════════════════════════════════════

/**
 * Ruta del asset SVG del sello de cumplimiento NOM-247.
 * Vive en `frontend/public/nom-seal.svg` — servido como `/nom-seal.svg`.
 *
 * Si el asset oficial aún no está disponible, se usa un placeholder
 * SVG temporal con forma de medalla (gradiente oro + cinta azul marino).
 * Documentar como deuda pendiente hasta reemplazar con asset oficial.
 */
export const NOM_SEAL_ASSET_PATH = '/nom-seal.svg'

/**
 * Copy oficial del sello — visible al visitante del sitio.
 *
 * REGLA CRÍTICA DE LENGUAJE:
 * - Nombre visible: "Cumplimiento NOM-247"
 * - Autoridad comunicada: la norma NOM-247-SE-2021 (no la certificadora)
 * - CERNAG NO aparece en ningún string visible. CERNAG es la entidad
 *   certificadora técnica — aparece solo en la URL de verificación
 *   (`NOM_CERT_VERIFY_URL`) que es técnicamente imprescindible.
 */
export const NOM_SEAL_COPY = {
  label: 'Cumplimiento NOM-247',
  altText: 'Sello de cumplimiento NOM-247',
  linkText: 'Ver sello',
  tooltip: 'Cumplimiento NOM-247 verificado',
  ariaLabel: 'Ver certificado de cumplimiento NOM-247',
} as const
